import PropTypes from "prop-types";
import { Fragment, useEffect, useState } from "react";
import { NavLink as RouterLink } from "react-router-dom";
import {
  Box,
  Collapse,
  List,
  ListItemText,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Iconify from "../Iconify";
import { StyledNavItem, StyledNavItemIcon } from "./styles";
import { useLocation } from "react-router-dom";

NavSection.propTypes = {
  data: PropTypes.array,
  isDesktop: PropTypes.bool,
  open: PropTypes.bool,
};

export default function NavSection({
  data = [],
  isDesktop,
  open,
  ...other
}: any) {
  const location = useLocation();
  return (
    <Box {...other}>
      <List disablePadding sx={{ p: 1 }}>
        {data.map((item: any) => (
          <NavItem
            key={item.title}
            item={item}
            isDesktop={isDesktop}
            open={open}
            pathname={location.pathname}
          />
        ))}
      </List>
    </Box>
  );
}

NavItem.propTypes = {
  item: PropTypes.object,
  isDesktop: PropTypes.bool,
  open: PropTypes.bool,
  pathname: PropTypes.string,
  depth: PropTypes.number,
};

function NavItem({ item, isDesktop, open, pathname, depth = 0 }: any) {
  const { title, path, icon, info, children = [] } = item;
  const theme = useTheme();
  const hasChildren = Array.isArray(children) && children.length > 0;
  const isActive =
    Boolean(path) && pathname === path
      ? true
      : hasChildren
        ? children.some(
            (child: any) =>
              child.path && pathname.startsWith(String(child.path)),
          )
        : false;
  const [expanded, setExpanded] = useState<boolean>(isActive);

  useEffect(() => {
    if (isActive) {
      setExpanded(true);
    }
  }, [isActive]);

  const content = (
    <StyledNavItem
      title={item.title}
      {...(!hasChildren && path
        ? {
            component: RouterLink,
            to: path,
          }
        : {
            onClick: () => setExpanded((prev) => !prev),
          })}
      sx={{
        ...(depth > 0
          ? {
              pl: 3 + depth,
              ml: 1,
              height: 42,
            }
          : {}),
        ...(isActive
          ? {
              color: "text.menu",
              bgcolor: "background.menu",
              fontWeight: theme.typography.fontWeightMedium,
            }
          : {}),
      }}
    >
      {icon && <StyledNavItemIcon>{icon}</StyledNavItemIcon>}

      {(!isDesktop || open) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ flex: 1 }}
        >
          <ListItemText disableTypography primary={title} />
          {hasChildren && (
            <Iconify
              icon={expanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"}
              width={18}
            />
          )}
        </Stack>
      )}

      {(!isDesktop || open) && info}
    </StyledNavItem>
  );

  return (
    <Fragment>
      {content}
      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ pl: 1 }}>
            {children.map((child: any) => (
              <NavItem
                key={child.title}
                item={child}
                isDesktop={isDesktop}
                open={open}
                pathname={pathname}
                depth={depth + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </Fragment>
  );
}
